import { EnterHook } from 'react-router';
import { store } from 'Internals/store';

import { SubActions, subSwitched } from 'Redux/thunks/subscriptions.thunks';
import { findDefaultUrl, findSubscription } from '../utils/subscription.utils';
import DatahubAPI from '../api/datahub.api';
import { PipeActions } from 'Redux/thunks/pipes.thunks';
import { DatasetActions } from 'Redux/thunks/datasets.thunks';
import { SystemActions } from 'Redux/thunks/systems.thunks';
import { datahubUpdated } from 'src/redux-store/thunks/datahub.thunks';

import { getConnectionString } from '../utils/subscription.utils';
import { CustomConnectionsThunk } from 'src/redux-store/thunks/customConnectionURL.thunks';

type EnterHookArgs = Parameters<EnterHook>;
type NextState = EnterHookArgs[0];
type Replace = EnterHookArgs[1];
type Callback = EnterHookArgs[2];

const dispatch = store.dispatch as any;

export const loadDatahubInfo = async (subId: string, callback?: () => void) => {
  try {
    const subscriptions = await dispatch(SubActions.loadAll());
    const currentSubscription = findSubscription(subscriptions.payload)(subId);

    const persistedURL = getConnectionString(subId);

    if (persistedURL) {
      dispatch(CustomConnectionsThunk.setCustomConnection(subId, persistedURL));
    }

    const defaultConnectionUrl = persistedURL ?? findDefaultUrl(currentSubscription) ?? undefined;

    const datahubInfo = defaultConnectionUrl
      ? await DatahubAPI.getDatahubInfoWithoutCredentials(defaultConnectionUrl)
      : undefined;

    if (datahubInfo) {
      dispatch(datahubUpdated(datahubInfo));
    }
  } catch (e) {
    console.error(e);
  } finally {
    callback?.();
  }
};

export const preLoadData = (subId: string, callback?: () => void) => {
  const connectSub = async () => {
    try {
      const { connected, token } = store.getState().subscription;

      if (!connected && !token) {
        const persistedURL = getConnectionString(subId);

        if (persistedURL) {
          dispatch(CustomConnectionsThunk.setCustomConnection(subId, persistedURL));
        }

        dispatch(SubActions.connect(subId));
      }
    } catch (e) {
      console.error(e);
    } finally {
      callback?.();
    }
  };

  connectSub();
};

export const preLoadSubscriptions = async (subId: string, callback?: () => void) => {
  try {
    const subscriptions = await dispatch(SubActions.loadAll());
    const currentSubscription = findSubscription(subscriptions.payload)(subId);

    dispatch(
      subSwitched({
        subscriptionId: subId,
        defaultConnectionUrl: findDefaultUrl(currentSubscription) ?? '',
        supportsLiveUpdates: currentSubscription?.supports_live_updates ?? false,
      })
    );
  } catch (e) {
    console.error(e);
  } finally {
    callback?.();
  }
};

/*
 * Fetching Datasets, Pipes and Systems.
 */
export const preLoadCoreData = async () => {
  await dispatch(DatasetActions.loadAll());
  await dispatch(PipeActions.loadAll());
  await dispatch(SystemActions.loadAll());
};

/*
 * Fetching Datasets, Pipes and Systems. If a route callback function is passed, the it will continue to the desired route once the requests has finished.
 */
export const onEnterLoadCoreData = (
  _nextState: NextState,
  _replace: Replace,
  callback?: Callback
) => {
  return async (connected: boolean) => {
    if (connected === true) {
      await preLoadCoreData();

      callback?.();
    } else {
      callback?.();
    }
  };
};
